import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { HeaderBar } from '@dealstack/frontend/shared/components/HeaderBar/HeaderBar';
import { ManageEmailAddress } from '@dealstack/frontend/shared/components/AccountSettings/ManageEmailAddress/ManageEmailAddress';
import { ManagePassword } from '@dealstack/frontend/shared/components/AccountSettings/ManagePassword/ManagePassword';
import { Manage2FA } from '@dealstack/frontend/shared/components/AccountSettings/Manage2FA/Manage2FA';
import {
	canChangeCredentials,
	getUserPersons,
	getUserProfile,
} from '@dealstack/frontend/shared/dataExchange/services/user.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { GenericError } from '@dealstack/frontend/shared/pages/GenericError/GenericError';
import { ACCOUNT_SETTING_SELECTION, SectionEditMode } from './AccountSettings.types';

interface Props {
	className?: string;
}

export const AccountSettings = ({ className }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const [sectionInEditMode, setSectionInEditMode] = useState<ACCOUNT_SETTING_SELECTION | null>(
		null
	);

	const editModeFor = (section: ACCOUNT_SETTING_SELECTION): SectionEditMode => ({
		isEditModeEnabled: sectionInEditMode === section,
		setIsEditModeEnabled: (enabled) =>
			setSectionInEditMode((current) => {
				if (enabled) {
					return section;
				}
				return current === section ? null : current;
			}),
	});

	const { data: userEmail } = useQuery({
		queryKey: [API_PATHS.USER_PROFILE_GET],
		queryFn: ({ signal }) => getUserProfile(API_PATHS.USER_PROFILE_GET, signal),
		select: (data) => data.payload?.email,
	});

	const { data: firstPersonEmail, isLoading: isLoadingPersonEmail } = useQuery({
		queryKey: ['personData'],
		queryFn: getUserPersons,
		select: (data) => data?.persons?.[0]?.email,
	});
	const { data: canChangeCredentialsResult, isLoading: isLoadingCanChangeCredentials } = useQuery(
		{
			queryKey: [API_PATHS.AUTH_CAN_CHANGE_CREDENTIALS_POST],
			queryFn: ({ signal }) =>
				canChangeCredentials(API_PATHS.AUTH_CAN_CHANGE_CREDENTIALS_POST, signal),
			select: (data) => data.payload,
		}
	);

	const email = firstPersonEmail ?? userEmail;

	if (isLoadingPersonEmail || isLoadingCanChangeCredentials) {
		return <FullscreenLoader name="pageLoading" />;
	}

	if (!canChangeCredentialsResult || !email) {
		return <GenericError />;
	}

	return (
		<div className={`flex flex-col ${className}`}>
			<HeaderBar
				className={className}
				tabSectionName={tCommonUi('profile.account_settings.section_name')}
			/>
			<div className="grid grid-cols-2 gap-4">
				<div>
					<ManageEmailAddress
						email={email}
						canChangeEmail={canChangeCredentialsResult.email}
						{...editModeFor(ACCOUNT_SETTING_SELECTION.MANAGE_EMAIL)}
						authenticationFactorId={canChangeCredentialsResult.authenticationFactorId}
					/>
					<div className="mt-4">
						<Manage2FA
							canSetup2FA={canChangeCredentialsResult.setup2FA}
							existingAuthenticationFactorId={
								canChangeCredentialsResult.authenticationFactorId
							}
							{...editModeFor(ACCOUNT_SETTING_SELECTION.MANAGE_2FA)}
						/>
					</div>
				</div>
				<ManagePassword
					email={email}
					canChangePassword={canChangeCredentialsResult.password}
					{...editModeFor(ACCOUNT_SETTING_SELECTION.MANAGE_PASSWORD)}
					authenticationFactorId={canChangeCredentialsResult.authenticationFactorId}
				/>
			</div>
		</div>
	);
};
